/**
 * ScanTasksIndicator — pilote global, monté dans Layout, qui :
 *  1. Poll toutes les 2,5 s les tâches d'analyse actives (serveur + local).
 *  2. Affiche un pill flottant discret « Analyse de la facture en cours… »
 *     quand au moins une tâche est queued / processing.
 *  3. Émet la notification cliquable de fin (review_required, completed)
 *     ET la notif d'échec réel (failed) uniquement.
 *  4. Nettoie automatiquement l'état local dès qu'une tâche a été acquittée
 *     côté serveur (via `DELETE /invoices/scan-tasks/{id}`).
 *
 * IMPORTANT : le composant vit dans Layout, PAS dans /factures.
 * L'analyse ne dépend donc jamais du cycle de vie de la page Factures.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ScanLine, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { scanTaskBus } from "../lib/scanTaskBus";

const POLL_MS = 2500;

const STATUS_LABEL = {
  queued: "Facture envoyée",
  processing: "Analyse en cours…",
  review_required: "Analyse terminée · Produits à vérifier",
  completed: "Analyse terminée",
  failed: "Impossible d'analyser la facture",
};

export default function ScanTasksIndicator() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  // Mémoire des statuts déjà affichés pour éviter les doublons de toast.
  // ⚠ Persisté en localStorage : sans ça, un F5 réinitialise la mémoire
  // et la même notification « Analyse terminée » ressurgit à chaque reload
  // tant que le backend renvoie encore la tâche non acquittée.
  const SEEN_KEY = "rentascan:scan-task-seen-status";
  const readSeen = () => {
    try { return new Map(Object.entries(JSON.parse(window.localStorage.getItem(SEEN_KEY) || "{}"))); }
    catch { return new Map(); }
  };
  const writeSeen = (map) => {
    try { window.localStorage.setItem(SEEN_KEY, JSON.stringify(Object.fromEntries(map))); }
    catch { /* quota */ }
  };
  const seenStatus = useRef(readSeen());

  // Poll — sans dépendre de la page Factures.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const { tasks: active = [] } = await api.listActiveScanTasks();
        if (cancelled) return;
        // Merge avec le bus local (au cas où le backend n'a pas encore
        // référencé la tâche renvoyée à l'utilisateur — sécurité).
        const knownIds = new Set(active.map((t) => t.id));
        for (const id of scanTaskBus.getIds()) {
          if (knownIds.has(id)) continue;
          // Interroger individuellement — peut avoir été acquittée.
          try {
            const doc = await api.getScanTask(id);
            active.push(doc);
          } catch {
            // 404 → tâche disparue, on la retire du bus.
            scanTaskBus.remove(id);
          }
        }

        // Émettre les transitions de statut (toast unique par task).
        // On filtre côté client aussi : si la facture liée est déjà validée
        // (statut != pending_review), on acquitte la tâche pour la retirer
        // définitivement de la liste et on n'émet PAS la notif.
        for (const t of active) {
          const prev = seenStatus.current.get(t.id);
          if (prev === t.status) continue;

          // Vérifie l'état réel de la facture avant de notifier — évite
          // les notifs sur factures déjà validées (l'utilisateur peut
          // valider directement depuis /factures sans passer par la notif).
          if ((t.status === "review_required" || t.status === "completed") && t.invoice_id) {
            try {
              const inv = await api.getInvoice(t.invoice_id);
              if (inv && inv.status !== "pending_review") {
                // Facture déjà traitée → on acquitte la tâche et on skip.
                try { await api.dismissScanTask(t.id); } catch {}
                seenStatus.current.set(t.id, "acknowledged");
                writeSeen(seenStatus.current);
                continue;
              }
            } catch { /* facture supprimée → on continue et on notifie quand même */ }
          }

          seenStatus.current.set(t.id, t.status);
          writeSeen(seenStatus.current);

          if (t.status === "review_required" || t.status === "completed") {
            const linesCount = t.lines_count ?? (t.extraction?.lines?.length ?? 0);
            const reviewCount = t.review_count ?? 0;
            const descBase = reviewCount > 0
              ? `${linesCount} produit(s) détecté(s), dont ${reviewCount} à vérifier.`
              : `${linesCount} produit(s) détecté(s).`;
            const supplier = t.supplier_name ? ` · ${t.supplier_name}` : "";
            const invRef = t.invoice_number ? ` #${t.invoice_number}` : (t.invoice_date ? ` du ${t.invoice_date}` : "");
            // Toute facture issue d'un scan OCR reste en statut pending_review
            // tant qu'elle n'est pas validée : on route donc systématiquement
            // vers l'écran de vérification complet (avec boutons "Valider les
            // produits"). La page /factures/:id/verification redirige seule
            // vers /factures/:id si la facture est déjà validée.
            const target = t.invoice_id
              ? `/factures/${t.invoice_id}/verification`
              : `/factures?task=${t.id}`;
            toast.success(STATUS_LABEL[t.status], {
              id: `scan-task-${t.id}`,
              description: `${descBase}${supplier}${invRef}`,
              duration: 12000,
              action: {
                label: "Voir la facture",
                onClick: () => {
                  navigate(target);
                  // Émet un évènement pour que la liste factures se rafraîchisse.
                  try { window.dispatchEvent(new CustomEvent("rentascan:invoices-changed", { detail: { source: "scan-task", task_id: t.id, invoice_id: t.invoice_id } })); } catch {}
                },
              },
            });
            // Auto-refresh la liste en tâche de fond même sans clic.
            try { window.dispatchEvent(new CustomEvent("rentascan:invoices-changed", { detail: { source: "scan-task-auto", task_id: t.id, invoice_id: t.invoice_id } })); } catch {}
          } else if (t.status === "failed") {
            toast.error(STATUS_LABEL.failed, {
              id: `scan-task-${t.id}`,
              description: t.error || "L'analyse OCR a échoué.",
              duration: 10000,
              action: {
                label: "Relancer",
                onClick: () => navigate("/factures"),
              },
            });
          } else if (t.status === "queued" && prev === undefined) {
            // Première fois qu'on voit la tâche → notif d'accusé réception.
            toast.info(STATUS_LABEL.queued, {
              id: `scan-task-${t.id}`,
              description: "Analyse en cours en arrière-plan.",
              duration: 4000,
            });
          }
        }

        setTasks(active);
      } catch {
        // Silencieux — pas de connexion, on retentera.
      } finally {
        if (!cancelled) timer = setTimeout(poll, POLL_MS);
      }
    };

    poll();

    // Rafraîchit aussi quand le bus local est modifié depuis un autre onglet
    // ou depuis /factures (add / remove).
    const unsub = scanTaskBus.subscribe(() => {
      if (timer) clearTimeout(timer);
      poll();
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [navigate]);

  const inFlight = tasks.filter((t) => t.status === "queued" || t.status === "processing");
  if (inFlight.length === 0) return null;

  return (
    <div
      data-testid="scan-tasks-indicator"
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#0B1F33] text-white rounded-full pl-3 pr-4 py-2 shadow-[0_8px_24px_rgba(11,31,51,0.25)] text-[12.5px] font-semibold"
    >
      <span className="relative flex items-center justify-center w-5 h-5">
        <Loader2 className="w-4 h-4 animate-spin text-[#FFD166]" />
      </span>
      <ScanLine className="w-3.5 h-3.5 opacity-70" />
      <span>
        {inFlight.length === 1
          ? "Analyse de la facture en cours…"
          : `${inFlight.length} analyses en cours…`}
      </span>
    </div>
  );
}
